import { OrganizationAdministration, OrganizationQuestionnaire } from "../../../api";
import { useAuth } from "../../../context";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";

export const organizationQuestionnaireKeys = {
  allDataEntryPlanningQuestionnaires: () => ["allDataEntryPlanningQuestionnaires"],
  dataEntryPlanningQuestionnaireById: () => ["dataEntryPlanningQuestionnaireById"],
  allOrgQuestionnairesByUser: () => ["allOrgQuestionnairesByUser"],
  orgQuestionnaireById: () => [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), "orgQuestionnaireById"],
  items: () => [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), "orgQuestionnaireById"],
};

export function useGetAllDataEntryPlanningQuestionnaires(queryParams, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires(), queryParams],
    () => callApi(OrganizationQuestionnaire.getAllQuestionnaires, queryParams),
    config
  );
}

export function useGetQuestionnaireById(id, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.dataEntryPlanningQuestionnaireById(), id],
    () => callApi(OrganizationQuestionnaire.getQuestionnaireById, id),
    config
  );
}

export function useCreateQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation((data) => callApi(OrganizationQuestionnaire.createQuestionnaire, data), config);
}

export function useUpdateQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(OrganizationQuestionnaire.updateQuestionnaire, { id, data }), config);
}

export function useDeleteQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(OrganizationQuestionnaire.deleteQuestionnaire, id), config);
}

export function useDefaultOrganizationQuestionnaireConfig() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();

  return {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  };
}

export function useFinalizeQuestionnaire(customConfig) {
  let config = useDefaultOrganizationQuestionnaireConfig();
  if (customConfig) {
    config = customConfig;
  }
  const { callApi } = useAuth();
  return useMutation((id) => callApi(OrganizationQuestionnaire.finalizeQuestionnaire, id), config);
}

export function useGetOrganizationStructure(config) {
  const { callApi } = useAuth();

  return useQuery(["orgStructure"], () => callApi(OrganizationAdministration.getOrganizationStructure), config);
}

export function useImportScopeOneAndTwoData(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ questionnaireId, data }) =>
      callApi(OrganizationQuestionnaire.importScopeOneAndTwoData, { questionnaireId, data }),
    config
  );
}

export function useGetOrganisationQuestionnaires(params, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), params.page, params.size],
    () => callApi(OrganizationQuestionnaire.getOrganisationQuestionnaires, params),
    config
  );
}

export function useGetQuestionnaireDataById(id, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.orgQuestionnaireById(), id],
    () => callApi(OrganizationQuestionnaire.getQuestionnaireDataById, id),
    config
  );
}

export function useUnfinaliseOrgQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(
    (questionnaireId) => callApi(OrganizationQuestionnaire.unfinaliseOrgQuestionnaire, questionnaireId),
    config
  );
}

export function useDownloadScopeOneTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(OrganizationQuestionnaire.downloadScopeOneTemplate), config);
}

export function useDownloadScopeTwoTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(OrganizationQuestionnaire.downloadScopeTwoTemplate), config);
}
